import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, ChevronDown, Check } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * Switch between the five admin panels.
 *
 * Two modes, because the two places it appears want different destinations:
 *
 *   <AppSwitcher />        on a login page — sends you to the *other* panel's login,
 *                          by the configured env URL, with a full page load.
 *   <AppSwitcher inApp />  in a header — sends you to the other panel's *dashboard*
 *                          by route. Ginox, Support, Gidex and Forex share one admin session,
 *                          so that is plain in-app navigation with no sign-in.
 *
 * Mining keeps its own backend and its own token, so its entry lands on the mining login.
 * That is expected — no badge for it; the login screen explains itself.
 */
const AppSwitcher = ({ inApp = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // useLocation, not window.location: its pathname has the router basename stripped, so
  // the active-app check keeps working if PUBLIC_URL is ever set.
  const path = location.pathname;
  const currentApp = path.startsWith("/mining")
    ? "mining"
    : path.startsWith("/forex-admin")
    ? "forex"
    : path.startsWith("/support-system")
    ? "support"
    : path.startsWith("/web3")
    ? "web3"
    : "ginox";

  const apps = [
    {
      id: "ginox",
      name: "Ginox Admin",
      description: "Main admin dashboard",
      route: "/",
      url: process.env.REACT_APP_GINOX_URL || "/auth/login",
    },
    {
      id: "mining",
      name: "Mining Admin",
      description: "Mining management",
      route: "/mining/dashboard",
      url: process.env.REACT_APP_MINING_URL || "/mining/login",
    },
    {
      id: "support",
      name: "Support Admin",
      description: "Support system",
      route: "/support-system/dashboard",
      url: process.env.REACT_APP_SUPPORT_URL || "/support-system/login",
    },
    {
      id: "web3",
      name: "Gidex Admin",
      description: "Gidex exchange",
      route: "/web3/combined",
      url: process.env.REACT_APP_WEB3_URL || "/web3/login",
    },
    {
      id: "forex",
      name: "Forex Admin",
      description: "Forex operations",
      route: "/forex-admin",
      url: process.env.REACT_APP_FOREX_ADMIN_URL || "/forex-admin/login",
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAppSwitch = (app) => {
    if (app.id !== currentApp) {
      if (inApp) navigate(app.route);
      else window.location.href = app.url;
    }
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
        aria-label="Switch application"
        aria-expanded={isOpen}
      >
        <LayoutGrid className="h-3.5 w-3.5" />
        {/* In a header the panel you are on is the useful label; on a login page there is
            no session to name yet, so it stays the invitation. "Admin" is dropped because
            the header already says so. */}
        <span>
          {inApp
            ? apps.find((a) => a.id === currentApp).name.replace(" Admin", "")
            : "Switch App"}
        </span>
        <ChevronDown className={cn("h-3 w-3 transition-transform", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-56 rounded-lg border border-border/70 bg-[#0f1117] shadow-xl overflow-hidden z-50">
          <div className="px-3 py-2 border-b border-border/40">
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/60">
              Switch Application
            </p>
          </div>
          <div className="py-1">
            {apps.map((app) => {
              const isActive = app.id === currentApp;
              return (
                <button
                  key={app.id}
                  onClick={() => handleAppSwitch(app)}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors",
                    isActive
                      ? "bg-ginox-green/5 text-foreground"
                      : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground"
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{app.name}</p>
                    <p className="text-xs text-muted-foreground/60 truncate">{app.description}</p>
                  </div>
                  {isActive && <Check className="h-3.5 w-3.5 shrink-0 text-ginox-green" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppSwitcher;
